import React, { useState, useEffect } from 'react';
import {
  Button, Col, DatePicker, Input, Modal, Row,
  Select, Spin, Table, Form, Typography, notification,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SquareEditOutlineIcon from 'mdi-react/SquareEditOutlineIcon';
import FilterIcon from 'mdi-react/FilterIcon';
import FilterRemoveIcon from 'mdi-react/FilterRemoveIcon';
import InformationOutlineIcon from 'mdi-react/InformationOutlineIcon';
import moment, { Moment } from 'moment';
import { namespaces } from '../../../../i18n/i18n.constants';
import { backendResponseCodes } from '../../../../utils/responseMessages';
import { CoopercitrusUser } from '../../../../sharedTypes/UserTypes';
import { Service } from '../../../../sharedTypes/ServiceTypes';
import { EstimateCustomer, EstimateFlow, EstimateStatus } from '../../../../sharedTypes/EstimateTypes';
import {
  put, patch, get, post, isSuperAgentError,
  RequestWrapper, ResponseWrapper,
} from '../../../../dataSource/requests';
import EstimateStatusComponent from '../../../sharedComponents/serviceStatusComponent/estimateStatusComponent/EstimateStatusComponent';
import { getUserType } from '../../../../utils/users';
import { formatBP } from '../../../../utils/format';
import './ListServiceEstimates.scss';

const ListServiceEstimates = (): JSX.Element => {
  interface RelevantPaginationInfo {
    current: number | undefined;
    defaultCurrent: number | undefined;
    pageSize: number | undefined;
    defaultPageSize: number | undefined;
    showQuickJumper: boolean | {
      goButton?: React.ReactNode;
    };
    total: number | undefined;
  }

  const { t } = useTranslation('namespaces');
  const navigate = useNavigate();
  const [searchFormInstance] = Form.useForm();
  const [userFormInstance] = Form.useForm();
  const [visibleModalStatus, setVisibleModalStatus] = useState<boolean>(false);
  const [loadingStatus, setLoadingStatus] = useState<boolean>(false);
  const [statusesList, setStatusesList] = useState<Array<EstimateFlow>>([]);
  const [canceled, setCanceled] = useState<boolean>(false);
  const [currentStatusIndex, setCurrentStatusIndex] = useState<number>(0);
  const [userType] = useState<string | null>(getUserType());
  const [loading, setLoading] = useState<boolean>(false);
  const [isTableLoading, setIsTableLoading] = useState<boolean>(false);
  const [servicesFilter, setServicesFilter] = useState<Array<JSX.Element>>([]);
  const [allActivatedUsers, setAllActivatedUsers] = useState<Array<CoopercitrusUser>>([]);
  const [userOptions, setUserOptions] = useState<Array<JSX.Element>>([]);
  const [statusArray, setStatusArray] = useState<Array<EstimateStatus>>([]);
  const [data, setData] = useState<Array<EstimateCustomer>>([]);
  const [maxPeriodOfDaysFilter, setMaxPeriodOfDaysFilter] = useState<number>(30);
  const [loadingExcel, setLoadingExcel] = useState<boolean>(false);

  const [paginationInfo, setPaginationInfo] = useState<RelevantPaginationInfo>({
    pageSize: 15,
    defaultPageSize: 15,
    current: 1,
    defaultCurrent: 1,
    showQuickJumper: true,
    total: 0,
  });

  const [visibleModalResponsable, setVisibleModalResponsable] = useState<boolean>(false);
  const [loadingModal, setLoadingModal] = useState<boolean>(false);
  const [userModalOptions, setUserModalOptions] = useState<Array<JSX.Element>>([]);
  const [currentModalServiceId, setCurrentModalServiceId] = useState<number>(0);

  const getFilterParameters = (): object => {
    const values = searchFormInstance.getFieldsValue();
    const params: {
      serviceName?: string;
      ctc?: string;
      bp?: string;
      landOwnerName?: string;
      landName?: string;
      responsableUserId?: number;
      serviceId?: number;
      statusId?: number;
      periodStart?: string;
      periodEnd?: string;
    } = {};

    if (values.serviceFilter) {
      params.serviceName = values.serviceFilter;
    }

    if (values.ctcFilter) {
      params.ctc = values.ctcFilter;
    }

    if (values.registrationFilter) {
      params.bp = values.registrationFilter;
    }

    if (values.cooperativeFilter) {
      params.landOwnerName = values.cooperativeFilter;
    }

    if (values.propertyFilter) {
      params.landName = values.propertyFilter;
    }

    if (values.userFilter) {
      params.responsableUserId = values.userFilter;
    }

    params.serviceId = values.serviceId || 0;
    params.statusId = values.statusFilter || null;

    if (values?.intervalFilter?.length) {
      params.periodStart = moment(values.intervalFilter[0])
        .startOf('day')
        .format('YYYY-MM-DDTHH:mm:ss.SSS');

      params.periodEnd = moment(values.intervalFilter[1])
        .endOf('day')
        .format('YYYY-MM-DDTHH:mm:ss.SSS');
    }

    return params;
  };

  const loadStatusData = async (id: number): Promise<void> => {
    setLoadingStatus(true);

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    try {
      const requestRef = get({
        url: `/budget/listBudgetStatusFlow/${id}`,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      response = await requestRef;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 13012): {
        // "Budget flow listed successfully", 13012
        const newStatusesList: Array<EstimateFlow> = response?.body?.budgetStatusFlowOutputs;
        setStatusesList(newStatusesList);
        let nextId;

        const findLastIndex = (array: Array<EstimateFlow>): number => {
          let finalIndex = 0;

          for (const ind in array) {
            if (array[ind].date != null) {
              finalIndex = ind;
            }
          }

          if (Number.parseInt(finalIndex, 10) + 1 === array.length) {
            nextId = null;
          } else {
            nextId = array[Number.parseInt(finalIndex, 10) + 1].id;
          }

          return finalIndex;
        };

        const statIndex = findLastIndex(newStatusesList);
        setCurrentStatusIndex(Number.parseInt(statIndex, 10));
        setLoadingStatus(false);

        if (
          (newStatusesList[statIndex]?.stepName === 'Orçamento recusado'
            || newStatusesList[statIndex]?.stepName === 'Orçamento expirado')
          && newStatusesList[statIndex].isDone === true
        ) {
          setCanceled(true);
        } else {
          setCanceled(false);
        }

        setLoadingStatus(false);
      } break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -14003):
        // "Budget not found", -14003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Budget
            .BudgetNotFound.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.body?.code === -4000):
      default:
        // "Something unexpected happened", -4000
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao buscar os detalhes, '
          + 'por favor tente novamente.',
        });
        break;
    }

    setLoadingStatus(false);
  };

  const showModalStatus = (id: number): void => {
    setVisibleModalStatus(true);
    loadStatusData(id);
  };

  const cleanFilterFields = (): void => {
    searchFormInstance.setFieldsValue({
      serviceFilter: '',
      matricularionFilter: '',
      cooperativeFilter: '',
      propertyFilter: '',
      userFilter: undefined,
      statusFilter: undefined,
      serviceId: undefined,
      intervalFilter: [moment().subtract(30, 'day'), moment()],
    });
  };

  const fetchData = async (page: number | undefined): Promise<void> => {
    setLoading(true);

    const params = getFilterParameters();

    let url = '/budget/customer/listAllBudgetRequest';

    let patchResponse: ResponseWrapper | undefined;
    let patchResponseError: ResponseWrapper | undefined;
    let patchGenericError: Error | undefined;

    if (userType === 'COOPERCITRUS') {
      url = '/budget/coopercitrus/listAllBudgetRequest';
    }

    try {
      const listAllBudgetRequestReqRef: RequestWrapper = patch({
        url,
        params,

        query: {
          page: (page || 1) - 1,
        },

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      patchResponse = await listAllBudgetRequestReqRef;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        patchResponseError = error.response;
      } else {
        patchGenericError = error as Error;
      }
    }

    switch (true) {
      case (patchResponse?.body?.code === 13002):
        // "All budget requests listed successfully", 13002
        if (userType === 'COOPERCITRUS') {
          setData(patchResponse?.body?.budgetRequests);
        } else if (userType === 'COOPERATIVE') {
          setData(patchResponse?.body?.budgetRequestViewOutputs);
        } else {
          setLoading(false);
          notification.error({
            message: 'Erro',
            description: 'Desculpe, houve um erro ao listar os orçamentos, '
              + 'por favor tente novamente mais tarde.',
          });
          return;
        }

        setPaginationInfo((previous) => {
          return ({
            ...previous,
            ...patchResponse?.body?.paginationInfo,
            pageSize: patchResponse?.body?.paginationInfo.maxSize,
            current: patchResponse?.body?.paginationInfo.pageNumber + 1,
            total: patchResponse?.body?.paginationInfo.totalElements,
          });
        });
        break;

      case (patchResponseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (patchResponseError?.body?.code === -1007):
        // "User not found", -1007
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.User
            .UserNotFound.description.pt_br,
        });

        setPaginationInfo((previous) => {
          return ({
            ...previous,
            current: 1,
            pageSize: 15,
            total: undefined,
          });
        });
        break;

      case (patchResponseError?.body?.code === -19000):
        // "SAPTimeOut", -19000
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPTimeOut', { ns: namespaces.error.sap }),
        });

        setPaginationInfo((previous) => {
          return ({
            ...previous,
            current: 1,
            pageSize: 15,
            total: undefined,
          });
        });
        break;

      case (patchResponseError?.body?.code === -19001):
        // "SAPNotFound", -19001
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPNotFound', { ns: namespaces.error.sap }),
        });

        setPaginationInfo((previous) => {
          return ({
            ...previous,
            current: 1,
            pageSize: 15,
            total: undefined,
          });
        });
        break;

      case (patchResponseError?.body?.code === -19002):
        // "SAPBadRequest", -19002
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPBadRequest', { ns: namespaces.error.sap }),
        });

        setPaginationInfo((previous) => {
          return ({
            ...previous,
            current: 1,
            pageSize: 15,
            total: undefined,
          });
        });
        break;

      case (patchResponseError?.body?.code === -18000):
        // "Bp not found of SAP", -18000
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.BP
            .BpNotFoundForSap.description.pt_br,
        });

        setPaginationInfo((previous) => {
          return ({
            ...previous,
            current: 1,
            pageSize: 15,
            total: undefined,
          });
        });
        break;

      case (patchResponseError?.body?.code === -14002):
        // "Invalid list budget requests filters", -14002
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Budget
            .InvalidListBudgetRequestsFilters.description.pt_br,
        });

        setPaginationInfo((previous) => {
          return ({
            ...previous,
            current: 1,
            pageSize: 15,
            total: undefined,
          });
        });
        break;

      case (patchResponseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });

        setPaginationInfo((previous) => {
          return ({
            ...previous,
            current: 1,
            pageSize: 15,
            total: undefined,
          });
        });
        break;

      case (patchGenericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        // "Something unexpected happened", -4000
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao buscar a listagem, '
          + 'por favor tente novamente mais tarde.',
        });

        setPaginationInfo((previous) => {
          return ({
            ...previous,
            current: 1,
            pageSize: 15,
            total: undefined,
          });
        });
        break;
    }

    setLoading(false);
  };

  const exportExcel = async (): Promise<void> => {
    setLoadingExcel(true);

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const paramsFilter = searchFormInstance.getFieldsValue();

    const params = {
      ctc: paramsFilter.ctcFilter == '' ? null : paramsFilter.ctcFilter,
      landName: paramsFilter.propertyFilter == '' ? null : paramsFilter.propertyFilter,
      landOwnerName: paramsFilter.cooperativeFilter == '' ? null : paramsFilter.cooperativeFilter,
      bp: paramsFilter.registrationFilter == '' ? null : paramsFilter.registrationFilter,
      periodEnd: paramsFilter.intervalFilter[1],
      periodStart: paramsFilter.intervalFilter[0],
      responsableUserId: paramsFilter.userFilter == '' ? null : paramsFilter.userFilter,
      serviceName: paramsFilter.serviceFilter == ''
        ? (
          paramsFilter.serviceId ? paramsFilter.serviceId : null)
        : paramsFilter.serviceFilter,
      statusId: paramsFilter.statusFilter == '' ? null : paramsFilter.statusFilter,
    };

    try {
      const exportExcelReqRef: RequestWrapper = post({
        url: '/budget/export/excel',
        params,

        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
        },

        responseType: 'blob',

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      response = await exportExcelReqRef;
    } catch (error: unknown) {
      console.log(error);
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.status === 200): {
        const filee = new Blob([response?.body], { type: 'xlsx' });

        const linkElement = document.createElement('a');
        const downloadUrl = window.URL.createObjectURL(filee);

        const today = new Date();
        const date = `${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}`;
        linkElement.href = downloadUrl;
        linkElement.download = `Usuarios ${date}.xlsx`;

        document.body.append(linkElement);

        linkElement.click();
        setLoadingExcel(false);

        setTimeout(() => {
          linkElement.remove();
          window.URL.revokeObjectURL(downloadUrl);
        }, 0);
      } break;

      case (responseError?.status === 403):
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.status === 400):
      case (responseError?.status === 408):
      default:
        // Bad request 400
        // Request timeout 408

        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao baixar o arquivo, por favor tente novamente mais tarde',
        });
        break;
    }

    setLoadingExcel(false);
  };

  const handleFilter = (): void => {
    fetchData(1);
  };

  const handleOkModal = async (): Promise<void> => {
    let values: {
      selectUser?: number;
    } = {};

    setLoadingModal(true);

    try {
      values = await userFormInstance.validateFields(['selectUser']);
    } catch (error: unknown) {
      console.log(error);
      return;
    }

    const params = {
      budgetRequestId: currentModalServiceId,
      userId: values.selectUser,
    };

    setLoadingModal(true);

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    try {
      const setResponsableUserReqRef: RequestWrapper = put({
        url: '/budget/setResponsableUser',
        params,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      response = await setResponsableUserReqRef;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 13006): {
        // "Responsable user set successfully", 13006
        let newData = data;

        const user = allActivatedUsers
          .find((v: CoopercitrusUser) => v.id === values.selectUser);

        if (user) {
          newData = newData.map((v: EstimateCustomer) => {
            if (v.id === currentModalServiceId) {
              return { ...v, responsableUserName: user.name };
            }
            return v;
          });

          setData(newData);
          setVisibleModalResponsable(false);
          setLoadingModal(false);
          userFormInstance.resetFields();
        } else {
          setVisibleModalResponsable(false);
          setLoadingModal(false);
          userFormInstance.resetFields();
          fetchData(1);
        }
      } break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        break;

      case (response?.body?.code === -14003):
        // "Budget not found", -14003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Budget
            .BudgetNotFound.description.pt_br,
        });
        break;

      case (response?.body?.code === -1007):
        // "User not found", -1007
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.User
            .UserNotFound.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao atribuir o usuário responsável, '
          + 'por favor tente novamente mais tarde.',
        });
        break;
    }

    setLoadingModal(false);
  };

  const showModalResponsable = (currentModalServiceIdParameter: number): void => {
    setCurrentModalServiceId(currentModalServiceIdParameter);
    setVisibleModalResponsable(true);
  };

  const validateDateRange = (_rule: any, value: Array<Moment>): Promise<void | Error> => {
    if (
      moment(value[1])
        .subtract(maxPeriodOfDaysFilter - 1, 'day').startOf('day')
      >= moment(value[0]).endOf('day')
    ) {
      return Promise.reject(new Error(`A busca é limitada a um intervalo de ${maxPeriodOfDaysFilter} dias`));
    }

    return Promise.resolve();
  };

  useEffect(() => {
    const loadStatusList = async (): Promise<void> => {
      let response: ResponseWrapper | undefined;
      let responseError: ResponseWrapper | undefined;
      let genericError: Error | undefined;

      try {
        const findAllReqRef = get({
          url: '/budget/findAllStatuses',

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        response = await findAllReqRef;
      } catch (error: unknown) {
        if (isSuperAgentError(error)) {
          responseError = error.response;
        } else {
          genericError = error as Error;
        }
      }

      switch (true) {
        case (response?.body?.code === 5004):
          // "All statuses listed successfully", 5004
          setStatusArray(response?.body?.statuses);
          break;

        case (responseError?.body?.code === -4001):
          // "You must be authenticated to perform this operation", -4001
          break;

        case (responseError?.body?.code === -4003):
          // "You do not have permission to perform this operation", -4003
          notification.error({
            message: 'Erro',
            description: backendResponseCodes.Error.General
              .Forbidden.description.pt_br,
          });
          break;

        case (genericError?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        case (responseError?.body?.code === -4000):
        default:
          notification.error({
            message: 'Erro',
            description: 'Desculpe, houve um erro ao carregar a listagem de status de serviços, '
            + 'por favor tente novamente mais tarde.',
          });
          break;
      }
    };

    const runOnMount = async (): Promise<void> => {
      await loadStatusList();

      let response1: ResponseWrapper | undefined;
      let response1Error: ResponseWrapper | undefined;
      let generic1Error: Error | undefined;

      try {
        const maxPeriodReqRef: RequestWrapper = get({
          url: '/service/getMaxPeriodOfDaysFilter',

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        response1 = await maxPeriodReqRef;
      } catch (error: unknown) {
        if (isSuperAgentError(error)) {
          response1Error = error.response;
        } else {
          generic1Error = error as Error;
        }
      }

      switch (true) {
        case (response1?.body?.code === 5012):
          // "Max period of days filter got successfully", 5012
          setMaxPeriodOfDaysFilter(response1?.body?.maxPeriodOfDaysFilter);
          break;

        case (response1Error?.body?.code === -4001):
          // "You must be authenticated to perform this operation", -4001
          break;

        case (response1Error?.body?.code === -4003):
          // "You do not have permission to perform this operation", -4003
          notification.error({
            message: 'Erro',
            description: backendResponseCodes.Error.General
              .Forbidden.description.pt_br,
          });
          break;

        case (generic1Error?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        case (response1Error?.body?.code === -4000):
        default:
          // "Something unexpected happened", -4000
          notification.error({
            message: 'Erro',
            description: 'Desculpe, houve um erro ao carregar o período máximo de dias, '
            + 'por favor tente novamente mais tarde.',
          });
          break;
      }

      if (userType === 'COOPERCITRUS') {
        let response2: ResponseWrapper | undefined;
        let response2Error: ResponseWrapper | undefined;
        let generic2Error: Error | undefined;

        let response3: ResponseWrapper | undefined;
        let response3Error: ResponseWrapper | undefined;
        let generic3Error: Error | undefined;

        try {
          const findAllReqRef: RequestWrapper = get({
            url: '/admin/findAllNotDeleted',

            frontConfigs: {
              navigateFunction: navigate,
            },
          });

          response2 = await findAllReqRef;
        } catch (error: unknown) {
          if (isSuperAgentError(error)) {
            response2Error = error.response;
          } else {
            generic2Error = error as Error;
          }
        }

        switch (true) {
          case (response2?.body?.code === 1009):
            // "All users listed successfully", 1009
            setUserOptions(
              response2?.body?.coopercitrusUsers.map(
                (v: CoopercitrusUser) => (
                  <Select.Option
                    key={v.id}
                    value={v.id}
                  >
                    {v.name}
                  </Select.Option>
                ),
              ),
            );
            break;

          case (response2Error?.body?.code === -1007):
            // "User not found", -1007
            notification.error({
              message: 'Erro',
              description: backendResponseCodes.Error.User
                .UserNotFound.description.pt_br,
            });
            break;

          case (response2Error?.body?.code === -4001):
            // "You must be authenticated to perform this operation", -4001
            break;

          case (response2Error?.body?.code === -4003):
            // "You do not have permission to perform this operation", -4003
            notification.error({
              message: 'Erro',
              description: backendResponseCodes.Error.General
                .Forbidden.description.pt_br,
            });
            break;

          case (generic2Error?.message?.includes('has been aborted')):
            // dont show anything when request is aborted
            break;

          case (response2Error?.body?.code === -4000):
          default:
            notification.error({
              message: 'Erro',
              description: 'Desculpe, houve um erro ao carregar a listagem de usuários '
              + 'responsáveis, por favor tente novamente mais tarde.',
            });
            break;
        }

        try {
          const requestRef: RequestWrapper = get({
            url: '/admin/findAllActivated',

            frontConfigs: {
              navigateFunction: navigate,
            },
          });

          response3 = await requestRef;
        } catch (error: unknown) {
          if (isSuperAgentError(error)) {
            response3Error = error.response;
          } else {
            generic3Error = error as Error;
          }
        }

        switch (true) {
          case (response3?.body?.code === 1009):
            // "All users listed successfully", 1009
            setAllActivatedUsers(response3?.body?.coopercitrusUsers);

            setUserModalOptions(
              response3?.body?.coopercitrusUsers.map(
                (v: CoopercitrusUser) => (
                  <Select.Option
                    key={v.id}
                    value={v.id}
                  >
                    {v.name}
                  </Select.Option>
                ),
              ),
            );
            break;

          case (response3Error?.body?.code === -1007):
            // "User not found", -1007
            notification.error({
              message: 'Erro',
              description: backendResponseCodes.Error.User
                .UserNotFound.description.pt_br,
            });
            break;

          case (response3Error?.body?.code === -4001):
            // "You must be authenticated to perform this operation", -4001
            break;

          case (response3Error?.body?.code === -4003):
            // "You do not have permission to perform this operation", -4003
            notification.error({
              message: 'Erro',
              description: backendResponseCodes.Error.General
                .Forbidden.description.pt_br,
            });
            break;

          case (generic3Error?.message?.includes('has been aborted')):
            // dont show anything when request is aborted
            break;

          case (response3Error?.body?.code === -4000):
          default:
            notification.error({
              message: 'Erro',
              description: 'Desculpe, houve um erro ao carregar a listagem de '
              + 'usuários responsáveis, por favor tente novamente mais tarde.',
            });
            break;
        }
      } else {
        let response4: ResponseWrapper | undefined;
        let response4Error: ResponseWrapper | undefined;
        let generic4Error: Error | undefined;

        try {
          const requestRef: RequestWrapper = get({
            url: '/service/findAllServicesFilter',

            frontConfigs: {
              navigateFunction: navigate,
            },
          });

          response4 = await requestRef;
        } catch (error: unknown) {
          if (isSuperAgentError(error)) {
            response4Error = error.response;
          } else {
            generic4Error = error as Error;
          }
        }

        switch (true) {
          case (response4?.body?.code === 5011):
            // "All services filter listed successfully", 5011
            setServicesFilter(
              response4?.body?.services.map(
                (v: Service) => (
                  <Select.Option
                    key={v.id}
                    value={v.name}
                  >
                    {v.name}
                  </Select.Option>
                ),
              ),
            );
            break;

          case (response4Error?.body?.code === -4001):
            // "You must be authenticated to perform this operation", -4001
            break;

          case (response4Error?.body?.code === -4003):
            // "You do not have permission to perform this operation", -4003
            notification.error({
              message: 'Erro',
              description: backendResponseCodes.Error.General
                .Forbidden.description.pt_br,
            });
            break;

          case (generic4Error?.message?.includes('has been aborted')):
            // dont show anything when request is aborted
            break;

          case (response4Error?.body?.code === -4000):
          default:
            notification.error({
              message: 'Erro',
              description: 'Desculpe, houve um erro ao carregar a listagem de serviços, '
              + 'por favor tente novamente mais tarde.',
            });
            break;
        }
      }

      fetchData(1);
    };

    runOnMount();

    return () => {
      setLoading(false);
    };
  }, []);

  const commonColumns = [
    {
      title: 'Propriedade',
      dataIndex: 'landName',
      key: 'landName',
      className: 'ListServiceEstimates__TableHeaderColumn',
    },
    {
      title: 'Cooperado',
      key: 'landOwnerName',
      dataIndex: 'landOwnerName',
      className: 'ListServiceEstimates__TableHeaderColumn',
    },
    {
      title: 'Serviço',
      dataIndex: 'serviceName',
      key: 'serviceName',
      className: 'ListServiceEstimates__TableHeaderColumn',
    },
    {
      title: 'Data/hora da solicitação',
      dataIndex: 'solicitationDateTime',
      key: 'solicitationDateTime',
      className: 'ListServiceEstimates__TableHeaderColumn',

      render: (date: string) => (date ? moment(date).format('DD/MM/YYYY [às] HH:mm') : ''),
    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'status',
      className: 'ListServiceEstimates__TableHeaderColumn',
      render: (textt: string, record: EstimateCustomer): JSX.Element => {
        if (textt === 'Orçamento recusado' || textt === 'Orçamento expirado') {
          return (
            <div
              className="status-container-btn"
            >
              <span>{textt}</span>
              <InformationOutlineIcon
                className="ListServiceEstimates__CancelledStatusIcon"
                onClick={
                  (): void => {
                    showModalStatus(record.id);
                  }
                }
              />
            </div>
          );
        }

        if (textt === 'Orçamento aprovado') {
          return (
            <div
              className="status-container-btn"
            >
              <span>{textt}</span>
              <InformationOutlineIcon
                className="ListServiceEstimates__ConcludedStatusIcon"
                onClick={
                  (): void => {
                    showModalStatus(record.id);
                  }
                }
              />
            </div>
          );
        }

        return (
          <div
            className="status-container-btn"
          >
            <span>{textt}</span>
            {
              userType === 'COOPERCITRUS'
                ? (
                  <a
                    href={`/estimate/status/${record.id}`}
                    className="ListServiceEstimates__StatusLink"
                    onClick={
                      (eventt): void => {
                        eventt.preventDefault();
                        navigate(`/estimate/status/${record.id}`);
                      }
                    }
                  >
                    <SquareEditOutlineIcon
                      className="ListServiceEstimates__EditStatusIcon"
                    />
                  </a>
                )
                : (
                  <SquareEditOutlineIcon
                    className="ListServiceEstimates__ShowStatusIcon"
                    onClick={
                      (): void => {
                        showModalStatus(record.id);
                      }
                    }
                  />
                )
            }
          </div>
        );
      },
    },
  ];

  const coopercitrusColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      className: 'ListServiceEstimates__TableHeaderColumn',
    },
    ...commonColumns,
    {
      title: 'Usuário Responsável',
      key: 'responsableUserName',
      dataIndex: 'responsableUserName',
      className: 'ListServiceEstimates__TableHeaderColumn',

      render: (responsableUserName: string, record: EstimateCustomer): JSX.Element => (
        <Button
          className="ListServiceEstimates__EditResponsibleUserButton"
          type="link"
          icon={(
            <SquareEditOutlineIcon />
          )}
          onClick={
            (): void => {
              showModalResponsable(record.id);
            }
          }
        >
          {responsableUserName}
        </Button>
      ),
    },
    {
      title: '',
      key: 'details',

      render: (_text: string, record: EstimateCustomer): JSX.Element => (
        <a
          className="ListServiceEstimates__DetailsButton"
          href={`/service/estimates/${record.id}/details`}
          onClick={
            (eventt): void => {
              eventt.preventDefault();
              navigate(`/service/estimates/${record.id}/details`);
            }
          }
        >
          Detalhes
        </a>
      ),
    },
  ];

  const cooperativeColumns = [
    ...commonColumns,
    {
      title: 'Matrícula',
      key: 'matriculation',
      dataIndex: 'matriculation',
      className: 'ListServiceEstimates__TableHeaderColumn',
    },
    {
      title: '',
      key: 'details',

      render: (_text: string, record: EstimateCustomer): JSX.Element => (
        <a
          className="ListServiceEstimates__DetailsButton"
          href={`/service/estimates/${record.id}/details`}
          onClick={
            (eventt): void => {
              eventt.preventDefault();
              navigate(`/service/estimates/${record.id}/details`);
            }
          }
        >
          Detalhes
        </a>
      ),
    },
  ];

  return (
    <div
      className="ListServiceEstimates"
    >
      <Spin
        className="ListServiceEstimates__Spin"
        tip="Carregando..."
        spinning={loading}
      >
        <Row
          gutter={[24, 24]}
          className="ListServiceEstimates__TitleRow"
          justify="space-between"
        >
          <Col
            className="ListServiceEstimates__TitleColumn"
          >
            <Typography.Title
              level={1}
              className="ListServiceEstimates__PageTitle"
            >
              {
                userType === 'COOPERCITRUS'
                  ? 'Controle de orçamentos solicitados'
                  : 'Orçamentos solicitados'
              }
            </Typography.Title>
          </Col>
          <Col
            className="ListServiceEstimates__NewEstimateButtonColumn"
          >
            <Row>
              <Button
                type="primary"
                htmlType="button"
                onClick={exportExcel}
                className="ListServiceEstimates__NewEstimateButton"
                loading={loadingExcel}
              >
                Exportar
              </Button>
              <Button
                className="ListServiceEstimates__NewEstimateButton"
                type="primary"
                onClick={
                  (): void => {
                    navigate('/service/new');
                  }
                }
              >
                Solicitar novo orçamento
              </Button>
            </Row>
          </Col>
        </Row>

        <Row
          gutter={24}
          className="ListServiceEstimates__FilterFormRow"
        >
          <Col
            xs={{ span: 24 }}
            className="ListServiceEstimates__FilterFormColumn"
          >
            <Form
              scrollToFirstError
              className="ListServiceEstimates__FilterForm"
              layout="vertical"
              form={searchFormInstance}
              onFinish={handleFilter}
              onFinishFailed={
                (errorInfo): void => {
                  console.log('onFinishFailed filterform', errorInfo);
                }
              }
            >
              <Row
                gutter={24}
                className="ListServiceEstimates__FilterFormChildRow ListServiceEstimates__FilterFormFirstChildRow"
              >
                {
                  userType === 'COOPERCITRUS'
                  && (
                    <>
                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 6 }}
                        className="ListServiceEstimates__FilterFormChildColumn ListServiceEstimates__UserFilterColumn"
                      >
                        <Form.Item
                          className="ListServiceEstimates__UserFilterFormItem"
                          label="Usuário responsável"
                          name="userFilter"
                          rules={[]}
                        >
                          <Select
                            className="ListServiceEstimates__UserFilterSelect"
                            showSearch
                            allowClear
                            style={{ width: '100%' }}
                            placeholder="Selecione o usuário responsável"
                            filterOption={
                              (input, option): boolean => option?.['props']
                                .children
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                          >
                            {userOptions}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 6 }}
                        className="ListServiceEstimates__RegistrationFilterColumn"
                      >
                        <Form.Item
                          className="ListServiceEstimates__RegistrationFilterFormItem"
                          label="BP"
                          name="registrationFilter"
                          rules={[]}
                          initialValue=""
                        >
                          <Input
                            maxLength={10}
                            onBlur={
                              (eventt): void => {
                                searchFormInstance.setFieldsValue({
                                  registrationFilter: eventt.target.value.replace(/.*/, formatBP(eventt.target.value)),
                                });
                              }
                            }
                            type="number"
                            placeholder="Digite o BP"
                            className="ListServiceEstimates__RegistrationFilterInput"
                          />
                        </Form.Item>
                      </Col>
                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 6 }}
                        className="ListServiceEstimates__CooperativeFilterColumn"
                      >
                        <Form.Item
                          className="ListServiceEstimates__CooperativeFilterFormItem"
                          label="Cooperado"
                          name="cooperativeFilter"
                          rules={[]}
                          initialValue=""
                        >
                          <Input
                            placeholder="Digite o cooperado"
                            className="ListServiceEstimates__CooperativeFilterInput"
                          />
                        </Form.Item>
                      </Col>
                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 6 }}
                        className="ListServiceEstimates__PropertyFilterColumn"
                      >
                        <Form.Item
                          className="ListServiceEstimates__PropertyFilterFormItem"
                          label="Propriedade"
                          name="propertyFilter"
                          rules={[]}
                          initialValue=""
                        >
                          <Input
                            placeholder="Digite a propriedade"
                            className="ListServiceEstimates__PropertyFilterInput"
                          />
                        </Form.Item>
                      </Col>
                    </>
                  )
                }

                <Col
                  xs={{ span: 24 }}
                  lg={{ span: 6 }}
                  className="ListServiceEstimates__ServiceFilterColumn"
                >
                  {
                    userType === 'COOPERCITRUS'
                      ? (
                        <Form.Item
                          className="ListServiceEstimates__ServiceNameFilterFormItem"
                          label="Serviço"
                          name="serviceFilter"
                          rules={[]}
                          initialValue=""
                        >
                          <Input
                            placeholder="Digite o serviço"
                            className="ListServiceEstimates__ServiceNameFilterInput"
                          />
                        </Form.Item>
                      )
                      : (
                        <Form.Item
                          className="ListServiceEstimates__ServiceIdFilterFormItem"
                          label="Serviço"
                          name="serviceFilter"
                          rules={[]}
                        >
                          <Select
                            className="ListServiceEstimates__ServiceIdFilterSelect"
                            showSearch
                            style={{ width: '100%' }}
                            placeholder="Selecione o serviço"
                          >
                            {servicesFilter}
                          </Select>
                        </Form.Item>
                      )
                  }
                </Col>

                <Col
                  xs={{ span: 24 }}
                  lg={{ span: 6 }}
                  className="ListServiceEstimates__StatusFilterColumn"
                >
                  <Form.Item
                    className="ListServiceEstimates__StatusFilterFormItem"
                    label="Status"
                    name="statusFilter"
                    rules={[]}
                  >
                    <Select
                      className="ListServiceEstimates__StatusFilterSelect"
                      showSearch
                      style={{ width: '100%' }}
                      placeholder="Selecione o status"
                    >
                      {
                        statusArray.map((v) => (
                          <Select.Option
                            key={v.id}
                            value={v.id}
                            className="ListServiceEstimates__StatusFilterSelectOption"
                          >
                            {v.name}
                          </Select.Option>
                        ))
                      }
                    </Select>
                  </Form.Item>
                </Col>
                <Col
                  xs={{ span: 24 }}
                  lg={{ span: 6 }}
                  className="ListServiceEstimates__IntervalFilterColumn"
                >
                  <Form.Item
                    className="ListServiceEstimates__IntervalFilterFormItem"
                    label="Período"
                    name="intervalFilter"
                    rules={[{ validator: validateDateRange }]}
                    initialValue={[moment().subtract(30, 'day'), moment()]}
                  >
                    <DatePicker.RangePicker
                      className="ListServiceEstimates__IntervalFilterDatePicker"
                      style={{ width: '100%' }}
                      placeholder={['Início', 'Fim']}
                      format="DD/MM/YYYY"
                    />
                  </Form.Item>
                </Col>

                {
                  userType === 'COOPERCITRUS'
                    ? (
                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 6 }}
                        className="ListServiceEstimates__CTCFilterColumn"
                      >
                        <Form.Item
                          className="ListServiceEstimates__CTCFilterFormItem"
                          label="Vendedor Responsável"
                          name="ctcFilter"
                          rules={[]}
                          initialValue=""
                        >
                          <Input
                            placeholder="Digite o nome"
                            className="ListServiceEstimates__CTCFilterInput"
                          />
                        </Form.Item>
                      </Col>
                    )
                    : null
                }
              </Row>

              <Row
                gutter={24}
                justify="end"
                className="ListServiceEstimates__FilterFormChildRow ListServiceEstimates__FilterFormSecondChildRow"
              >
                <Col
                  xs={{ span: 24 }}
                  lg={{ span: 6 }}
                  className="ListServiceEstimates"
                >
                  <Row
                    className="ListServiceEstimates__ButtonsRow"
                    gutter={8}
                  >
                    <Col
                      xs={{ span: 12 }}
                      lg={{ span: 12 }}
                      className="ListServiceEstimates__ResetButtonColumn"
                    >
                      <Form.Item
                        className="ListServiceEstimates__ResetButtonFormItem"
                        label=" "
                        colon={false}
                      >
                        <Button
                          block
                          className="ListServiceEstimates__ResetButton"
                          onClick={
                            (): void => {
                              cleanFilterFields();
                            }
                          }
                          htmlType="reset"
                        >
                          <FilterRemoveIcon
                            className="ListServiceEstimates__ResetButtonIcon"
                          />
                          <span
                            className="ListServiceEstimates__ResetButtonText"
                          >
                            Limpar
                          </span>
                        </Button>
                      </Form.Item>
                    </Col>
                    <Col
                      xs={{ span: 12 }}
                      lg={{ span: 12 }}
                      className="ListServiceEstimates__FilterButtonColumn"
                    >
                      <Form.Item
                        className="ListServiceEstimates__FilterButtonFormItem"
                        label=" "
                        colon={false}
                      >
                        <Button
                          block
                          className="ListServiceEstimates__FilterButton"
                          type="primary"
                          htmlType="submit"
                        >
                          <FilterIcon
                            className="ListServiceEstimates__FilterButtonIcon"
                          />
                          <span
                            className="ListServiceEstimates__FilterButtonText"
                          >
                            Filtrar
                          </span>
                        </Button>
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>

        <Row
          gutter={24}
          className="ListServiceEstimates__TableParentRow"
        >
          <Col
            span={24}
            className="ListServiceEstimates__TableParentColumn"
          >
            <Table
              className="ListServiceEstimates__Table"
              rowKey="id"
              columns={
                userType === 'COOPERCITRUS'
                  ? coopercitrusColumns
                  : cooperativeColumns
              }
              loading={isTableLoading}
              dataSource={data}
              pagination={{
                pageSize: paginationInfo.pageSize || 15,
                current: paginationInfo.current || 1,
                defaultCurrent: paginationInfo.defaultCurrent || 1,
                showQuickJumper: paginationInfo.showQuickJumper || true,
                total: paginationInfo.total || 0,
              }}
              onChange={
                (pagination): void => {
                  setIsTableLoading(true);

                  const pager = paginationInfo;

                  pager.current = pagination.current;

                  setPaginationInfo((previous) => {
                    return ({
                      ...previous,
                      ...pager,
                    });
                  });

                  setIsTableLoading(false);

                  fetchData(pagination.current);
                }
              }
            />
          </Col>
        </Row>

        <Modal
          className="ListServiceEstimates__StatusModal"
          wrapClassName="ListServiceEstimates-status-modal"
          width={1000}
          title={<h3 className="ListServiceEstimates__StatusModalTitle" style={{ margin: 0 }}>Status da solicitação</h3>}
          visible={visibleModalStatus}
          onCancel={
            (): void => {
              setVisibleModalStatus(false);
            }
          }
          footer={[]}
        >
          {
            visibleModalStatus
            && !loadingStatus
            && (
              <EstimateStatusComponent
                loading={loadingStatus}
                statusesList={statusesList}
                currentStatusIndex={currentStatusIndex}
                canceled={canceled}
              />
            )
          }
        </Modal>

        <Modal
          wrapClassName="ListServiceEstimates__UserModal"
          title={<h3 style={{ margin: 0 }}>Atribuir usuário</h3>}
          visible={visibleModalResponsable}
          onOk={handleOkModal}
          onCancel={
            (): void => {
              setVisibleModalResponsable(false);
            }
          }
          confirmLoading={loadingModal}
          footer={[
            <Button
              className="ListServiceEstimates__UserModalOkButton btn -ok"
              type="primary"
              key="submit"
              loading={loadingModal}
              onClick={handleOkModal}
            >
              Salvar
            </Button>,
          ]}
        >
          {
            visibleModalResponsable
            && (
              <Form
                scrollToFirstError
                className="ListServiceEstimates__UserForm"
                form={userFormInstance}
              >
                <Form.Item
                  className="ListServiceEstimates__ResponsibleUserFormItem"
                  label=""
                  name="selectUser"
                  rules={[{ required: true, message: 'Por favor, insira o usuário responsável.' }]}
                  initialValue={undefined}
                >
                  <Select
                    className="ListServiceEstimates__ResponsibleUserSelect"
                    showSearch
                    allowClear
                    style={{ width: '100%' }}
                    placeholder="Digite o usuário responsável"
                    filterOption={
                      (input, option): boolean => option?.['props']
                        .children
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {userModalOptions}
                  </Select>
                </Form.Item>
              </Form>
            )
          }
        </Modal>
      </Spin>
    </div>
  );
};

ListServiceEstimates.propTypes = {};
ListServiceEstimates.defaultProps = {};

export default ListServiceEstimates;
